import type { MessageResponse } from '@/domain/Api';
import type { Cart } from '@/domain/Cart';
import { api } from '@/lib/api';
import { queryOptions } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';

export interface CartsPaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface CartsResponse extends MessageResponse {
  data: Array<Cart>;
  meta?: CartsPaginationMeta;
}

export interface CartResponse extends MessageResponse {
  data: Cart;
}

export const cartsQuery = queryOptions({
  queryKey: queryKeys.carts.all(),
  queryFn: async () => api.get('carts').json<CartsResponse>(),
});

export const cartQuery = (cartId: number) =>
  queryOptions({
    queryKey: queryKeys.carts.cart(cartId),
    queryFn: async () => api.get(`carts/${cartId}`).json<CartResponse>(),
  });

export const getUserCartsQuery = (userId: number, page = 1) =>
  queryOptions({
    queryKey: queryKeys.carts.adminPatientCarts(userId, page),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set('user_id', String(userId));
      searchParams.set('page', String(page));
      searchParams.set('per_page', '100');

      return api
        .get('admin/patient-carts', { searchParams: searchParams.toString() })
        .json<CartsResponse>();
    },
  });

// doctorId undefined → backend scopes by auth (doctor role)
// doctorId provided  → admin selecting a specific doctor
export const getPatientCartsQuery = (doctorId?: number, page = 1) =>
  queryOptions({
    queryKey: queryKeys.carts.patientCarts(doctorId, page),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set('state', 'Waiting');
      searchParams.set('page', String(page));
      searchParams.set('per_page', '15');
      if (doctorId !== undefined) {
        searchParams.set('doctor_id', String(doctorId));
      }

      return api
        .get('admin/patient-carts', { searchParams: searchParams.toString() })
        .json<CartsResponse>();
    },
  });
